import { ActionType } from '../../stores/action-types';
import Node from '@material-ui/icons/Memory';
import Refresh from '@material-ui/icons/Refresh';
import CustomButton from '../../../components/CustomButtons/ButtonTS';
import Button from '@material-ui/core/Button';
import Card from '../../../components/Card/CardTS';
import CardBody from '../../../components/Card/CardBody';
import CardHeader from '../../../components/Card/CardHeaderTS';
import CardIcon from '../../../components/Card/CardIcon.js';
import React, { useCallback } from 'react';
import regularFormsStyle from '../../../assets/jss/material-dashboard-pro-react/views/regularFormsStyleTS';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { bindDeviceToNode, getFreeDevices } from '../../stores/actions/installerActions';
import { getSystemsNodesShort } from '../../stores/actions/logsActions';
import { connect } from 'react-redux';
import Loading from '../../components/Loading/Loading';
import Snackbar from '../../../components/Snackbar/Snackbar';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import ErrorText from '../../components/ErrorComponent/ErrorText';
import './InstallerPanel.css';
import { headerStyleSuccess, headerStyleFail, emptyHeaderStyle } from '../../helpers/GlobalVariables';
import { SYSTEM_VERSIONS } from '../../helpers/Systems';
import { ISystem } from '../../helpers/interfaces';
import { IFreeDevice } from './CreateNode/definitions';

interface IBindDeviceToNodeForm {
    classes: any;
    bindDeviceToNode: (nodeId: number, serialString: string) => void;
    getFreeDevices: () => void;
    getSystemsNodesShort: () => void;
    systemsNodesShort: any[]; // now expected to include deviceSerial per node
    FreeDevices: IFreeDevice[];
    systems: ISystem[];
    disableRefreshButton: boolean;
    bindDeviceFail: boolean;
    bindDeviceSuccess: boolean;
    mobileView: boolean;
}

const BindDeviceToNode = ({
    classes,
    bindDeviceToNode,
    getFreeDevices,
    getSystemsNodesShort,
    systemsNodesShort,
    FreeDevices,
    systems,
    disableRefreshButton,
    bindDeviceFail,
    bindDeviceSuccess,
    mobileView,
}: IBindDeviceToNodeForm) => {
    const dispatch = useDispatch();
    const closeNotificationTimer = useRef(null);

    const [pickedSystemId, setPickedSystemId]     = useState<number>(null);
    const [pickedNodeId, setPickedNodeId]         = useState<number>(null);
    const [pickedSerial, setPickedSerial]         = useState<string>(null);

    const freeDevices = FreeDevices.filter(
        (device) => device.SystemVersion === SYSTEM_VERSIONS.W4.valueOf()
    );

    const closeNotification = useCallback(() => {
        dispatch({ type: ActionType.CLOSE_BIND_DEVICE_NOTIFICATION });
    }, [dispatch]);

    useEffect(() => {
        getFreeDevices();
        getSystemsNodesShort();
        closeNotification();
    }, [getFreeDevices, getSystemsNodesShort, closeNotification]);

    useEffect(() => {
        if (bindDeviceFail || bindDeviceSuccess) {
            clearTimeout(closeNotificationTimer.current);
            closeNotificationTimer.current = setTimeout(closeNotification, 6000);
        }
        return () => clearTimeout(closeNotificationTimer.current);
    }, [bindDeviceFail, bindDeviceSuccess, closeNotification]);

    useEffect(() => {
        if (bindDeviceSuccess) {
            setPickedSystemId(null);
            setPickedNodeId(null);
            setPickedSerial(null);
        }
    }, [bindDeviceSuccess]);

    const systemNamesByIds = useMemo(() =>
        Object.fromEntries(systems.map(x => [x.ID, x.name])),
        [systems]
    );
    const nodeNamesByIds = useMemo(() =>
        Object.fromEntries(systemsNodesShort.map(x => [x.nodeId, x.nodeName])),
        [systemsNodesShort]
    );
    const systemNodes = useMemo(() =>
        systemsNodesShort.filter(s => s.systemId == pickedSystemId),
        [systemsNodesShort, pickedSystemId]
    );

    const handlePickSystem = (_e: React.ChangeEvent<{}>, newValue: number): void => {
        setPickedSystemId(newValue ?? null);
        setPickedNodeId(null);
        setPickedSerial(null);
    };

    const handlePickNode = (_e: React.ChangeEvent<{}>, newValue: number): void => {
        if (!newValue) {
            setPickedNodeId(null);
            return;
        }
        setPickedNodeId(newValue);
        setPickedSerial(null);
    };

    const handlePickDevice = (_e: React.ChangeEvent<{}>, newValue: string): void => {
        setPickedSerial(newValue ?? null);
    };

    const handleSubmitClick = (): void => {
        closeNotification();
        bindDeviceToNode(pickedNodeId, pickedSerial);
    };

    const nodeOptions = systemNodes.map(n => n.nodeId);

    const deviceOptions: string[] = freeDevices.map((d) => d.SerialString).sort();

    const isSubmitDisabled = !pickedSystemId || !pickedNodeId || !pickedSerial || bindDeviceFail || bindDeviceSuccess;

    const renderSubmitButton = (): JSX.Element => {
        return (
            <Button
                style={{ marginTop: '25px', transition: 'color 0.3s' }}
                disabled={isSubmitDisabled}
                onClick={handleSubmitClick}
                data-testid={'bindDeviceButton'}
                color={'primary'}
            >
                {'Bind'}
            </Button>
        );
    };

    const renderRefreshButton = (): JSX.Element => {
        if (disableRefreshButton) {
            return (
                <CustomButton
                    disabled
                    simple
                    justIcon
                    color='info'
                    style={{ width: '40px', paddingLeft: '10px', paddingRight: '10px' }}
                >
                    <Loading transform={1} height={17} width={20} color='blue' />
                </CustomButton>
            );
        }
        return (
            <CustomButton simple justIcon color='info' onClick={() => getFreeDevices()} disabled={disableRefreshButton}>
                <Refresh />
            </CustomButton>
        );
    };

    const renderMobileNotifications = (): JSX.Element => {
        if (!mobileView) return null;
        const failed = bindDeviceFail;
        const succeeded = bindDeviceSuccess;
        if (failed || succeeded) {
            const headerStyle = failed ? headerStyleFail : headerStyleSuccess;
            const message = bindDeviceFail
                ? 'The device could not be bound. Maybe it is already assigned.'
                : 'Device bound to node successfully.';
            return (
                <h5
                    className={failed ? 'notification-open-bind-device-fail' : 'notification-open-bind-device-success'}
                    style={headerStyle}
                >
                    {message}
                </h5>
            );
        }
        return <div style={emptyHeaderStyle}></div>;
    };

    try {

        if (systems.length < 1) {
            return (
                <Card>
                    <CardHeader color='info' icon>
                        <CardIcon color='info'>
                            <Node />
                        </CardIcon>
                        <h4 className={classes.cardIconTitle}>Bind Device to Node</h4>
                    </CardHeader>
                    <CardBody>Please add a system first.</CardBody>
                </Card>
            );
        }

        return (
            <>
            <ErrorComponent message='bind device to node form crashed' Children>
                <Card className='createNodeForm'>
                    <CardHeader color='info' icon>
                        <CardIcon color='info'>
                            <Node />
                        </CardIcon>
                        <h4 className={classes.cardIconTitle} data-testid='bindDeviceHeader'>
                            Bind Device to Node
                        </h4>
                    </CardHeader>
                    <CardBody>
                        <div style={{ width: '100%' }}>
                            <Autocomplete
                                options={systems.map(s => s.ID)}
                                getOptionLabel={(id) => systemNamesByIds[id] ?? ''}
                                id='bind-system-name'
                                value={pickedSystemId}
                                onChange={handlePickSystem}
                                renderInput={(params) => (
                                    <TextField {...params} label='System' margin='normal' />
                                )}
                                style={{ width: mobileView ? '100%' : '50%', transition: '0.3s' }}
                            />
                        </div>

                        <div style={{ width: '100%' }}>
                            <Autocomplete
                                options={nodeOptions}
                                getOptionLabel={(id) => nodeNamesByIds[id] ?? ''}
                                id='bind-node-name'
                                value={pickedNodeId}
                                onChange={handlePickNode}
                                disabled={!pickedSystemId}
                                renderInput={(params) => (
                                    <TextField {...params} label='Node' margin='normal' />
                                )}
                                style={{ width: mobileView ? '100%' : '50%', transition: '0.3s' }}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                            <Autocomplete
                                options={deviceOptions}
                                getOptionLabel={(device) => device}
                                id='available-devices'
                                value={pickedSerial}
                                onChange={(event, newValue) => handlePickDevice(event, newValue)}
                                renderInput={(filteredDevices) => <TextField {...filteredDevices} label='Device serial number' margin='normal' />}
                                style={{ width: mobileView ? '100%' : '50%', transition: '0.3s' }}
                            />
                            {renderRefreshButton()}
                        </div>
                        {renderMobileNotifications()}
                        {renderSubmitButton()}
                    </CardBody>

                    {!mobileView ? (
                        <Snackbar
                            close
                            closeNotification={closeNotification}
                            color={bindDeviceFail ? 'danger' : 'success'}
                            message={bindDeviceFail
                                ? 'The device could not be bound. Maybe it is already assigned.'
                                : 'Device bound to node successfully.'
                            }
                            open={bindDeviceFail || bindDeviceSuccess}
                            place='tc'
                        />
                    ) : null}
                </Card>
            </ErrorComponent>
            </>
        );
    } catch (error) {
        return <ErrorText message='Bind Device to Node form crashed' />;
    }
};

const mapStateToProps = (state) => ({
    FreeDevices:          state.dashboard.FreeDevices,
    disableRefreshButton: state.dashboard.disableRefreshButton,
    bindDeviceFail:       state.dashboard.bindDeviceFail,
    bindDeviceSuccess:    state.dashboard.bindDeviceSuccess,
    mobileView:           state.dashboard.mobileView,
    systemsNodesShort:    state.dashboard.systemsNodesShort
});

const actions = { bindDeviceToNode, getFreeDevices, getSystemsNodesShort };

const BindDeviceToNodeWithStyles = regularFormsStyle(BindDeviceToNode);
export default connect(mapStateToProps, actions)(BindDeviceToNodeWithStyles);